<script setup lang="ts">
import { Question } from "../components/fields";
import Input from "../components/fields/Input.vue";
import useVuelidate from "@vuelidate/core";
import { captureException } from "@sentry/vue";
import { useQuickQuoteCalculatorStore } from "@/stores/quickQuoteCalculator";
import { quickQuoteValidator } from "@/utils/validators";
import { formatCurrency } from "@/composables/formatCurrency";
import Loader from "@/components/Loader.vue";
import Card from "@/components/Card.vue";
import { computed, reactive, ref } from "vue";
import axios from "axios";
import Accordion from "@/components/Accordion.vue";
import { getSessionId } from "@/utils";
import { useRoute } from "vue-router";
import Error from "./Error.vue";
import { z } from "zod";

const quickQuoteCalculatorStore = useQuickQuoteCalculatorStore();
const validator = quickQuoteValidator(quickQuoteCalculatorStore);
const v$ = useVuelidate(validator, quickQuoteCalculatorStore);
const bestAnnualPensionDetails = ref<{
    bestPremiumAnnual: number
    bestPremiumMonthly: number
}>({
    bestPremiumAnnual: 0,
    bestPremiumMonthly: 0,
});
const state = reactive({
    loading: false,
    error: false,
    results: false,
});
const route = useRoute();
const isSeoPage = computed(() => {
    const queryParameter = route.query.isSeo;
    return queryParameter === "true";
});
const formattedMonthlyPension = computed(() => formatCurrency(bestAnnualPensionDetails.value.bestPremiumMonthly, 0));
const formattedYearlyPension = computed(() => formatCurrency(bestAnnualPensionDetails.value.bestPremiumAnnual, 0));
async function handleSubmitClick() {
    const valid = await v$.value.$validate();
    if (valid) {
        state.error = false;
        state.results = false;
        state.loading = true;
        bestAnnualPensionDetails.value = {
            bestPremiumAnnual: 0,
            bestPremiumMonthly: 0,
        };
        try {
            const response = await axios.post("/quick-quote", {
                isSeo: isSeoPage.value,
                dateOfBirth: quickQuoteCalculatorStore.estimatedDateOfBirth,
                postcode: quickQuoteCalculatorStore.postcode,
                fundValue: quickQuoteCalculatorStore.fundValue,
                sessionId: getSessionId(),
            }, {
                timeout: 60000,
            });
            const quickQuoteResponseSchema = z.object({
                bestPremiumDetails: z.object({
                    bestPremiumAnnual: z.number(),
                    bestPremiumMonthly: z.number(),
                }),
            });
            const bestPremiumDetails = quickQuoteResponseSchema.safeParse(response.data);
            if (bestPremiumDetails.success === false) {
                captureException(bestPremiumDetails.error);
                state.loading = false;
                state.error = true;
                return;
            }
            bestAnnualPensionDetails.value = bestPremiumDetails.data.bestPremiumDetails;
            state.loading = false;
            state.results = true;
        } catch (exception) {
            captureException(exception);
            state.error = true;
            state.loading = false;
        }
    }
}

</script>
<template>
    <div>
        <div
            v-if="state.results === false"
            id="quick-quote-calculator"
        >
            <div
                class="m-3 flex-center text-center flex-column"
            >
                <h1 class="mt-4">
                    Calculate your annuity
                </h1>
                <p class="mt-4">
                    Get an estimated annuity income in 30 seconds. No phone calls, no obligation.
                </p>
            </div>
            <form class="col-12">
                <div class="row">
                    <Question
                        name="age"
                        label="How old are you?"
                        sub-label="Our calculator is available for customers aged 55-84, as
                            these are the ages we can currently provide annuity quotes for."
                        input-class="col-12"
                    >
                        <Input
                            id="age"
                            v-model="quickQuoteCalculatorStore.age"
                            type="number"
                            :validation="validator.age"
                            class="col-12"
                        />
                    </Question>
                    <Question
                        name="postcode"
                        label="What's your postcode?"
                        sub-label="Some annuity providers use location information when calculating rates."
                        input-class="col-12"
                    >
                        <Input
                            id="postcode"
                            v-model="quickQuoteCalculatorStore.postcode"
                            type="text"
                            :validation="validator.postcode"
                            class="col-12"
                        />
                    </Question>
                    <Question
                        name="fund-value"
                        label="How much is your pension worth?"
                        input-class="col-12"
                    >
                        <Input
                            id="fund-value"
                            v-model.number="quickQuoteCalculatorStore.fundValue"
                            type="currency"
                            inputmode="numeric"
                            :validation="validator.fundValue"
                            class="col-12"
                        />
                    </Question>
                    <div
                        class="col-12 flex-center flex-column"
                    >
                        <button
                            v-if="state.loading === false && state.error === false"
                            id="submit-button"
                            class="btn btn-info mt-2"
                            @click.prevent="handleSubmitClick"
                        >
                            Calculate
                        </button>
                        <div
                            v-if="state.loading"
                            class="loader-wrapper mb-4"
                        >
                            <Loader
                                id="loader"
                                :inline="true"
                            />
                        </div>
                        <Error
                            v-if="state.error === true && state.loading === false"
                            id="quick-quote-error"
                            :link="'/quick-quote'"
                            :link-type="'anchor'"
                        />
                    </div>
                </div>
            </form>
        </div>
        <div
            v-if="state.results === true"
            id="quick-quote-calculator-results"
        >
            <div
                class="m-4 flex-center text-center flex-column"
            >
                <h1 class="mt-4">
                    Your estimated annuity
                </h1>
            </div>
            <Card
                :background-color="'var(--primary-green)'"
                :border-radius-px="16"
                class="mb-4"
            >
                <div
                    class="flex-center flex-column white-text"
                >
                    <h1
                        id="result"
                        class="currency-title text-center"
                    >
                        {{ formattedMonthlyPension }} <span class="currency-frequency">per month</span>
                    </h1>
                    <p>
                        or approximately {{ formattedYearlyPension }} per year
                    </p>
                    <a
                        id="recalculate-button"
                        href="#"
                        class="m-2 white-text"
                        @click.prevent="state.results = false; state.error = false; state.loading = false"
                    >
                        Recalculate annuity
                    </a>
                </div>
            </Card>
            <div
                class="flex-center"
            >
                <button
                    id="pension-annuity-calculator-get-your-personalised-quote-iframe-cta"
                    class="btn btn-info m-4 quick-quote-calculator-cta"
                    onclick="window.open('/', '_blank')"
                >
                    Get Your Personalised Quote
                </button>
            </div>
            <Accordion
                class="mt-4"
                :accordion-content="[{title: 'How we\'ve calculated your estimate', open: false}]"
            >
                <template #accordion-0>
                    <div class="mt-4">
                        <p>This calculator provides an illustrative estimate only and is based on the following assumptions:</p>
                        <ul class="mt-3">
                            <li>A married male</li>
                            <li>Taking 25% tax-free cash</li>
                            <li>No medical conditions</li>
                            <li>An annuity that pays income monthly in advance</li>
                            <li>No guaranteed payment period</li>
                            <li>No dependant's pension</li>
                            <li>No value protection</li>
                            <li>No annual increases to income.</li>
                        </ul>
                        <p>Your actual annuity income could be higher or lower depending on your age, health, lifestyle, pension value, and the options you choose.</p>
                    </div>
                </template>
            </Accordion>
        </div>
    </div>
</template>
<style scoped>
.loader-wrapper {
    height: 80px;
    width: 80px;
}

h1::after {
    content: "";
    display: block;
    width: 30%;
    height: 5px;
    background-color: var(--primary-blue);
    left: 35%;
    top: 10px;
    position: relative;
}

.flex-center {
    display: flex;
    align-content: center;
    align-items: center;
    justify-content: center;
}

.flex-column {
    flex-direction: column;
}

.white-text {
    color: white
}

.currency-title {
    font-size: 2.3rem;
    font-weight: bold;

}

.currency-title::after {
    content: "";
    display: none;
}

.currency-frequency {
    font-size: 1rem;
    font-weight: normal;
}

#recalculate-button {
    text-decoration: underline white !important;
    font-size: 1rem;
    font-weight: normal;
}

.quick-quote-calculator-cta {
    font-size: 1rem;
    font-weight: normal;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    background-color: var(--cta-button-blue);
    color: white;
    border: none;
}

.quick-quote-calculator-cta:hover, .quick-quote-calculator-cta:active {
    background-color: var(--cta-button-hover-blue);
}
</style>
